import classNames from "classnames";
import { ComponentPropsWithoutRef, ElementType } from "react";

import { useMenuContext } from "./MenuContext";

export type MenuItemProps<T extends ElementType> = {
  as?: T;
  onSelect?: () => void;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "onSelect">;

/**
 * A single menu entry. Renders as a <button> by default, or any element/
 * component via `as` (e.g. react-router's Link). Closes the menu whenever
 * it's activated, after calling the passed onSelect/onClick.
 */
function MenuItem<T extends ElementType = "button">({
  as,
  onSelect,
  onClick,
  className,
  children,
  ...rest
}: MenuItemProps<T>) {
  const { close, open } = useMenuContext();
  const Component = as || "button";

  const handleClick = (e: any) => {
    onClick?.(e);
    onSelect?.();
    close();
  };

  const typeProp = Component === "button" ? { type: "button" as const } : {};

  return (
    <Component
      {...rest}
      {...typeProp}
      role="menuitem"
      tabIndex={open ? (rest as any).tabIndex : -1}
      className={classNames("Menu__item", className)}
      onClick={handleClick}
    >
      {children}
    </Component>
  );
}

export default MenuItem;
